import React, { memo } from "react";
import { CardContent, Typography, Box, FormControlLabel, Checkbox } from "@mui/material";

interface SummaryItem {
  title: string;
  value: number | string;
  color: string;
}

interface FilterOption {
  label: string;
  value: string;
}

interface OffersSummaryCardProps {
  filters: any;
  applicationFilters: any;
  summaryData: SummaryItem[];
  filterOptions: FilterOption[];
  onCardClick?: (statusTitle: string) => void;
  onFilterChange?: (key: string, value: any) => void;
}

const FilterCheckbox: React.FC<{
  label: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
}> = memo(({ label, checked, onChange }) => (
  <FormControlLabel
    control={<Checkbox checked={checked} onChange={(_, checked) => onChange(checked)} />}
    label={
      <Typography fontSize="0.875rem" variant="h6">
        {label}
      </Typography>
    }
    sx={{
      whiteSpace: "nowrap",
      marginRight: 0,
      display: "flex",
      alignItems: "center",
    }}
  />
));

const OffersSummaryCard: React.FC<OffersSummaryCardProps> = ({
  filters,
  applicationFilters,
  summaryData,
  filterOptions,
  onCardClick,
  onFilterChange,
}) => {
  return (
    <CardContent sx={{ p: 1 }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "nowrap",
          overflowX: "auto",
          gap: 1,
          pb: 1,
          ml: 0.5,
          "&::-webkit-scrollbar": {
            height: "6px",
          },
          "&::-webkit-scrollbar-track": {
            background: "#f1f1f1",
            borderRadius: "10px",
          },
          "&::-webkit-scrollbar-thumb": {
            background: "#888",
            borderRadius: "10px",
          },
          "&::-webkit-scrollbar-thumb:hover": {
            background: "#555",
          },
        }}
      >
        {summaryData.map((item, index) => {
            const statusMap: Record<string, string> = {
              "All Offers": "",
              "Accepted Offers": "accepted",
              "Timed Out Offers": "timed out",
              "Offers Pending": "pending",
              "Pending Response": "pending response",
              "Response Sent": "response sent",
            };
          
            const itemStatus = statusMap[item.title] || "";
            const isActive = filters.status === itemStatus;
          return (
            <Box
              key={index}
              sx={{
                flex: "1 0 auto",
                minWidth: "110px",
                height: 100,
                p: 1,
                textAlign: "center",
                mt: 1,
                position: "relative",
                border: isActive ? `2px solid #2F929F` : `1px solid ${item.color}40`,
                borderRadius: 2,
                backgroundColor: isActive ? `${item.color}20` : `${item.color}08`,
                transition: "all 0.2s",
                cursor: "pointer",
                boxShadow: isActive ? `0 0 6px 2px ${item.color}40` : 0,
                "&:hover": {
                  transform: "translateY(-2px)",
                  boxShadow: isActive ? `0 0 8px 3px ${item.color}55` : 2,
                  backgroundColor: `${item.color}12`,
                },
              }}
              onClick={() => onCardClick?.(item.title)}
            >
              <Typography
                variant="h6"
                sx={{
                  color: item.color,
                  fontSize: "2.0rem",
                  fontWeight: 700,
                  lineHeight: 1.0,
                  textAlign: "center",
                  mt: 1,
                  mb: 0,
                }}
              >
                {item.value}
              </Typography>

              <Typography
                variant="caption"
                sx={{
                  fontFamily: "Roboto",
                  fontWeight: 400,
                  color: item.color,
                  fontSize: "0.9rem",
                  lineHeight: 1.0,
                  textAlign: "center",
                  mt: 0,
                }}
              >
                {item.title}
              </Typography>
            </Box>
          );
        })}
      </Box>

      <Box display="flex" alignItems="center" gap={2} flexWrap="nowrap" px={2} mt={1} mb={-4}>
        {filterOptions.map((filter) => (
          <FilterCheckbox
            key={filter.value}
            label={filter.label}
            checked={!!applicationFilters[filter.value]}
            onChange={(checked) => onFilterChange?.(filter.value, checked)}
          />
        ))}
      </Box>
    </CardContent>
  );
};

export default OffersSummaryCard;
